import {
  BeddingConfigurationExtra,
  ExtraGuestGeneratedPolicies,
  ExtraGuestGeneratedPolicyItem,
  InclusionItem,
} from '~/types/roomPolicy';

interface StructuredResult {
  isStructured: true;
  data: ExtraGuestGeneratedPolicies;
}

interface UnstructuredResult {
  isStructured: false;
  data: null;
}

type ParseResult = StructuredResult | UnstructuredResult;

function isExtraGuestGeneratedPolicies(value: unknown): value is ExtraGuestGeneratedPolicies {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const obj = value as Record<string, unknown>;

  return (
    Array.isArray(obj.maximumRoomCapacities) &&
    Array.isArray(obj.packageInclusions) &&
    Array.isArray(obj.extraGuestGeneratedPolicyItems) &&
    Array.isArray(obj.additionalPolicyNotes)
  );
}

function normalizeBeddingExtra(item: unknown): BeddingConfigurationExtra | null {
  if (typeof item === 'string') {
    return { bedding: item, icon: '' };
  }
  if (typeof item === 'object' && item !== null && 'bedding' in item) {
    const obj = item as Record<string, unknown>;
    return {
      bedding: typeof obj.bedding === 'string' ? obj.bedding : '',
      icon: typeof obj.icon === 'string' ? obj.icon : '',
    };
  }
  return null;
}

function normalizeInclusion(item: unknown): InclusionItem | null {
  if (typeof item === 'string') {
    return { inclusion: item, icon: '' };
  }
  if (typeof item === 'object' && item !== null && 'inclusion' in item) {
    const obj = item as Record<string, unknown>;
    return {
      inclusion: typeof obj.inclusion === 'string' ? obj.inclusion : '',
      icon: typeof obj.icon === 'string' ? obj.icon : '',
    };
  }
  return null;
}

function normalizeGuestPolicyItem(item: ExtraGuestGeneratedPolicyItem): ExtraGuestGeneratedPolicyItem {
  return {
    ...item,
    inclusions: Array.isArray(item.inclusions)
      ? item.inclusions.map(normalizeInclusion).filter((i): i is InclusionItem => i !== null)
      : [],
  };
}

export default function parseRoomPolicyDescription(description: string): ParseResult {
  if (!description || typeof description !== 'string') {
    return { isStructured: false, data: null };
  }

  try {
    const parsed = JSON.parse(description);

    if (isExtraGuestGeneratedPolicies(parsed)) {
      const data: ExtraGuestGeneratedPolicies = {
        ...parsed,
        beddingConfiguration: typeof parsed.beddingConfiguration === 'string' ? parsed.beddingConfiguration : '',
        beddingConfigurationExtras: Array.isArray(parsed.beddingConfigurationExtras)
          ? parsed.beddingConfigurationExtras
              .map(normalizeBeddingExtra)
              .filter((item): item is BeddingConfigurationExtra => item !== null)
          : [],
        extraGuestGeneratedPolicyItems: Array.isArray(parsed.extraGuestGeneratedPolicyItems)
          ? parsed.extraGuestGeneratedPolicyItems.map(normalizeGuestPolicyItem)
          : [],
      };
      return { isStructured: true, data };
    }

    return { isStructured: false, data: null };
  } catch {
    return { isStructured: false, data: null };
  }
}
